// Shared keystroke handling + scoring for all round variants.
//
// Original implementation lived inline in LessonScreen's ActiveRound.
// This hook lifts the strict-input behavior, hint computation and result
// building so DictationRound, EchoRound, MetronomeRound and ShadowRound
// can reuse it without divergence.
//
// Behavior preserved 1:1 from the original ActiveRound:
//
//   - STRICT mode: wrong key counts as mistake but does NOT advance cursor
//   - Hesitation hints (recall):  >=1500ms soft, >=3000ms strong
//   - Challenge: only nudge after >=3 mistakes + 1500ms idle
//   - Case-insensitive letter compare; space matches space
//   - response times clamped to 10s

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { charFromAltKeyEvent } from '../data/altLayer';
import type {
  KeyboardLayoutId,
  PracticeMode,
  RoundPresentation,
  RoundResult,
} from '../types';
import type { HintLevel } from '../components/Keyboard';
import { accuracy as accuracyFn, wpm as wpmFn } from './stats';
import {
  charsMatch,
  errorKeyForChar,
  isPrintableTypingKey,
  shouldIgnoreKeyEvent,
} from './typingKey';
import { bigramKey } from './typingProfile';
import { splitWords, wordIndexAtCursor } from './wordSpans';

export interface UseRoundInputArgs {
  targetText: string;
  mode: PracticeMode;
  roundId: string;
  roundTitle: string;
  presentationKind?: RoundPresentation['kind'];
  /** Called when the round finishes (completed or Escaped). */
  onFinish: (r: RoundResult) => void;
  /**
   * Optional callback fired on each correct keystroke. Useful e.g. for
   * dictation to speak the next word when a word boundary is crossed.
   */
  onAdvance?: (info: {
    cursor: number;
    char: string;
    completed: boolean;
  }) => void;
  /** When false, the keystroke handler is detached. Default true. */
  enabled?: boolean;
  /** Require exact letter case (niveau 5+). */
  strictCase?: boolean;
  /** Accept Alt/Option @ etc. (bonus e-mail). */
  allowAltLayer?: boolean;
  keyboardLayoutId?: KeyboardLayoutId;
  /** Do not finish when cursor reaches end — extend targetText instead. */
  continueOnComplete?: boolean;
  /** Let the caller invoke finish() after completion (e.g. brief success flash). */
  deferFinishOnComplete?: boolean;
  /** End the round immediately on the first mistake. */
  failOnFirstMistake?: boolean;
  /** Fired when the user presses a wrong key. */
  onMistake?: (info: {
    typed: string;
    expected: string;
    cursor: number;
  }) => void;
  /** Merged into RoundResult when the round finishes. */
  buildResultExtras?: () => Partial<RoundResult>;
}

export interface UseRoundInputState {
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  totalKeystrokes: number;
  mistakes: number;
  elapsedMs: number;
  liveWpm: number;
  liveAcc: number;
  expectedChar: string | undefined;
  hintLevel: HintLevel;
  lastPress: { key: string; correct: boolean } | undefined;
  /** True once strong hint is currently active (used to show finger name). */
  strongHintActive: boolean;
  /** Finish the round programmatically (e.g. external time limit). */
  finish: (completed: boolean) => void;
  /** True after the round has finished. Handler is detached. */
  finished: boolean;
}

export function useRoundInput({
  targetText,
  mode,
  roundId,
  roundTitle,
  presentationKind,
  onFinish,
  onAdvance,
  enabled = true,
  strictCase = false,
  allowAltLayer = false,
  keyboardLayoutId = 'mac-dk',
  continueOnComplete = false,
  deferFinishOnComplete = false,
  failOnFirstMistake = false,
  onMistake,
  buildResultExtras,
}: UseRoundInputArgs): UseRoundInputState {
  const onFinishRef = useRef(onFinish);
  const onAdvanceRef = useRef(onAdvance);
  const onMistakeRef = useRef(onMistake);
  const buildResultExtrasRef = useRef(buildResultExtras);
  useEffect(() => {
    onFinishRef.current = onFinish;
    onAdvanceRef.current = onAdvance;
    onMistakeRef.current = onMistake;
    buildResultExtrasRef.current = buildResultExtras;
  }, [onFinish, onAdvance, onMistake, buildResultExtras]);

  const words = useMemo(() => splitWords(targetText), [targetText]);

  const stateRef = useRef({
    cursor: 0,
    correctMask: new Array<boolean>(targetText.length).fill(false),
    correctChars: 0,
    totalKeystrokes: 0,
    mistakes: 0,
    backspaces: 0,
    errorsByKey: {} as Record<string, number>,
    responseTimes: {} as Record<string, number[]>,
    errorsByBigram: {} as Record<string, number>,
    transitionTimes: {} as Record<string, number[]>,
    wordStats: {} as Record<
      string,
      { mistakes: number; totalMs: number; count: number }
    >,
    activeWordIdx: -1,
    wordMistakes: 0,
    wordStartedAt: 0,
    expectedShownAt: 0,
    startedAtMs: 0,
    finished: false,
    hintsUsed: false,
  });

  const [, setTick] = useState(0);
  const [lastPress, setLastPress] = useState<
    { key: string; correct: boolean } | undefined
  >(undefined);
  const [now, setNow] = useState<number>(Date.now());

  const forceRerender = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const s = stateRef.current;
    if (targetText.length > s.correctMask.length) {
      s.correctMask.push(
        ...new Array(targetText.length - s.correctMask.length).fill(false),
      );
    }
  }, [targetText]);

  const recordWordCompletion = useCallback(
    (word: string, durationMs: number, mistakes: number) => {
      const s = stateRef.current;
      if (!s.wordStats[word]) {
        s.wordStats[word] = { mistakes: 0, totalMs: 0, count: 0 };
      }
      s.wordStats[word].mistakes += mistakes;
      if (durationMs > 0) {
        s.wordStats[word].totalMs += durationMs;
        s.wordStats[word].count += 1;
      }
    },
    [],
  );

  const finalizeActiveWord = useCallback(() => {
    const s = stateRef.current;
    if (s.activeWordIdx < 0 || s.activeWordIdx >= words.length) return;
    const w = words[s.activeWordIdx];
    const duration =
      s.wordStartedAt > 0 ? Date.now() - s.wordStartedAt : 0;
    recordWordCompletion(w.text, duration, s.wordMistakes);
    s.activeWordIdx = -1;
    s.wordMistakes = 0;
    s.wordStartedAt = 0;
  }, [words, recordWordCompletion]);

  const beginWord = useCallback(
    (idx: number) => {
      const s = stateRef.current;
      if (idx < 0 || idx >= words.length) return;
      if (s.activeWordIdx === idx) return;
      if (s.activeWordIdx >= 0) finalizeActiveWord();
      s.activeWordIdx = idx;
      s.wordMistakes = 0;
      s.wordStartedAt = Date.now();
    },
    [words, finalizeActiveWord],
  );

  const computeHintLevel = useCallback(
    (idleMs: number): HintLevel => {
      const s = stateRef.current;
      if (mode === 'guided') return 'strong';
      if (mode === 'recall') {
        if (idleMs >= 3000) return 'strong';
        if (idleMs >= 1500) return 'soft';
        return 'none';
      }
      if (s.mistakes >= 3 && idleMs >= 1500) return 'soft';
      return 'none';
    },
    [mode],
  );

  const finish = useCallback(
    (completed: boolean) => {
      const s = stateRef.current;
      if (s.finished) return;
      s.finished = true;
      const endedAtMs = Date.now();
      const durationMs = s.startedAtMs === 0 ? 0 : endedAtMs - s.startedAtMs;
      if (s.activeWordIdx >= 0) finalizeActiveWord();

      const result: RoundResult = {
        roundId,
        title: roundTitle,
        mode,
        targetLength: targetText.length,
        durationMs,
        wpm: wpmFn(s.correctChars, durationMs),
        accuracy: accuracyFn(s.correctChars, s.totalKeystrokes),
        correctChars: s.correctChars,
        totalKeystrokes: s.totalKeystrokes,
        mistakes: s.mistakes,
        backspaces: s.backspaces,
        errorsByKey: { ...s.errorsByKey },
        responseTimes: cloneRTs(s.responseTimes),
        errorsByBigram:
          Object.keys(s.errorsByBigram).length > 0
            ? { ...s.errorsByBigram }
            : undefined,
        transitionTimes:
          Object.keys(s.transitionTimes).length > 0
            ? cloneRTs(s.transitionTimes)
            : undefined,
        wordStats:
          Object.keys(s.wordStats).length > 0
            ? { ...s.wordStats }
            : undefined,
        completed,
        hintsUsed: s.hintsUsed,
        presentationKind: presentationKind ?? 'standard',
        ...buildResultExtrasRef.current?.(),
      };
      onFinishRef.current(result);
    },
    [
      roundId,
      roundTitle,
      mode,
      targetText.length,
      presentationKind,
      finalizeActiveWord,
    ],
  );

  const applyTypedChar = useCallback(
    (typed: string) => {
      const s = stateRef.current;
      if (s.finished) return;

      if (s.startedAtMs === 0) {
        s.startedAtMs = Date.now();
        s.expectedShownAt = s.startedAtMs;
      }

      const expected = targetText[s.cursor];
      if (expected === undefined) return;
      s.totalKeystrokes += 1;

      const same = charsMatch(typed, expected, strictCase);

      if (same) {
        const prevCursor = s.cursor;
        const wordIdx = wordIndexAtCursor(words, prevCursor);
        if (wordIdx >= 0) beginWord(wordIdx);

        const dt = Date.now() - s.expectedShownAt;
        const expectedKey = errorKeyForChar(expected, strictCase);
        if (!s.responseTimes[expectedKey]) {
          s.responseTimes[expectedKey] = [];
        }
        s.responseTimes[expectedKey].push(Math.min(dt, 10_000));

        if (prevCursor > 0) {
          const prevChar = targetText[prevCursor - 1]!;
          const bg = bigramKey(prevChar, expected);
          if (!s.transitionTimes[bg]) s.transitionTimes[bg] = [];
          s.transitionTimes[bg].push(Math.min(dt, 10_000));
        }

        s.correctMask[s.cursor] = true;
        s.correctChars += 1;
        s.cursor += 1;
        s.expectedShownAt = Date.now();
        setLastPress({ key: typed, correct: true });
        forceRerender();

        if (
          wordIdx >= 0 &&
          prevCursor < words[wordIdx].end &&
          s.cursor >= words[wordIdx].end
        ) {
          finalizeActiveWord();
        }

        const completed = s.cursor >= targetText.length;
        onAdvanceRef.current?.({
          cursor: s.cursor,
          char: expected,
          completed,
        });
        if (completed && !continueOnComplete && !deferFinishOnComplete) {
          finish(true);
        }
      } else {
        s.mistakes += 1;
        const expectedKey = errorKeyForChar(expected, strictCase);
        s.errorsByKey[expectedKey] =
          (s.errorsByKey[expectedKey] ?? 0) + 1;
        if (s.cursor > 0) {
          const prevChar = targetText[s.cursor - 1]!;
          const bg = bigramKey(prevChar, expected);
          s.errorsByBigram[bg] = (s.errorsByBigram[bg] ?? 0) + 1;
        }
        const mistakeWordIdx = wordIndexAtCursor(words, s.cursor);
        if (mistakeWordIdx >= 0) {
          beginWord(mistakeWordIdx);
          s.wordMistakes += 1;
        }
        setLastPress({ key: typed, correct: false });
        forceRerender();
        onMistakeRef.current?.({
          typed,
          expected,
          cursor: s.cursor,
        });
        if (failOnFirstMistake) finish(false);
      }
    },
    [
      targetText,
      words,
      finish,
      forceRerender,
      beginWord,
      finalizeActiveWord,
      strictCase,
      continueOnComplete,
      deferFinishOnComplete,
      failOnFirstMistake,
    ],
  );

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      const s = stateRef.current;
      if (s.finished) return;
      if (shouldIgnoreKeyEvent(e)) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        s.backspaces += 1;
        forceRerender();
        return;
      }

      if (e.key === 'Dead') return;

      if (allowAltLayer) {
        const altChar = charFromAltKeyEvent(e, keyboardLayoutId);
        if (altChar) {
          e.preventDefault();
          applyTypedChar(altChar);
          return;
        }
      }

      if (!isPrintableTypingKey(e.key)) return;
      if (e.isComposing) return;
      e.preventDefault();
      applyTypedChar(e.key);
    };

    const onCompositionEnd = (e: CompositionEvent) => {
      if (!e.data) return;
      const char = [...e.data.normalize('NFC')];
      if (char.length !== 1) return;
      e.preventDefault();
      applyTypedChar(char[0]!);
    };

    window.addEventListener('keydown', handler);
    window.addEventListener('compositionend', onCompositionEnd);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('compositionend', onCompositionEnd);
    };
  }, [
    enabled,
    finish,
    forceRerender,
    applyTypedChar,
    allowAltLayer,
    keyboardLayoutId,
  ]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const s = stateRef.current;
      const t = Date.now();
      setNow(t);
      if (s.startedAtMs > 0) {
        const idleMs = t - s.expectedShownAt;
        if (computeHintLevel(idleMs) !== 'none') {
          s.hintsUsed = true;
        }
      }
    }, 150);
    return () => window.clearInterval(id);
  }, [computeHintLevel]);

  const s = stateRef.current;
  const elapsedMs = s.startedAtMs === 0 ? 0 : now - s.startedAtMs;
  const liveWpm = wpmFn(s.correctChars, elapsedMs);
  const liveAcc = accuracyFn(s.correctChars, s.totalKeystrokes);
  const expectedChar = targetText[s.cursor];
  const idleMs = s.startedAtMs === 0 ? 0 : now - s.expectedShownAt;
  const hintLevel = computeHintLevel(idleMs);
  const strongHintActive =
    hintLevel === 'strong' && mode !== 'guided' && expectedChar !== undefined;

  return {
    cursor: s.cursor,
    correctMask: s.correctMask,
    totalKeystrokes: s.totalKeystrokes,
    mistakes: s.mistakes,
    elapsedMs,
    liveWpm,
    liveAcc,
    expectedChar,
    hintLevel,
    lastPress,
    strongHintActive,
    finish,
    finished: s.finished,
  };
}

function cloneRTs(rts: Record<string, number[]>): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  for (const [k, v] of Object.entries(rts)) out[k] = [...v];
  return out;
}
